import { QUERY, useSuspenseQueryOperation } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { useUserPermissionInfo } from 'ts/base/hooks/PermissionInfoHook';
import { useProjectId } from 'ts/base/hooks/UseProject';
import { TeamscaleLink } from 'ts/base/routing/TeamscaleLink';
import { Links } from 'ts/commons/links/Links';
import { PathUtils } from 'ts/commons/PathUtils';
import { Button, ButtonGroup } from 'ts/components/Button';
import { useBlacklistPermissions } from 'ts/perspectives/findings/details/tabs/commons/UseBlacklistPermissions';
import {
	BlacklistButton,
	CreateTaskButton,
	UnblacklistButton
} from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabBlacklistButtons';
import { getComparisonLink } from 'ts/perspectives/findings/details/tabs/FindingDetailsSiblingsTabContent';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import { EFindingBlacklistType } from 'typedefs/EFindingBlacklistType';
import { EProjectPermission } from 'typedefs/EProjectPermission';
import type { TrackedFinding } from 'typedefs/TrackedFinding';
import type { TrackedFindingWithDiffInfo } from 'typedefs/TrackedFindingWithDiffInfo';

/**
 * Props for FindingSiblingBatchActions consisting of the finding (of the detail view), the blacklist option for the
 * project, the siblings, the commit, and a change listener propagating the blacklist state changes back to the parent
 * view
 */
type SiblingBatchBlacklistButtonGroupProps = {
	finding: TrackedFindingWithDiffInfo;
	siblingFindings: TrackedFinding[];
	commit: UnresolvedCommitDescriptor;
};

/** The batch actions apply their effect (blacklist, unblacklist, add to task) to the finding and its siblings */
export function SiblingBatchBlacklistButtonGroup({
	finding,
	siblingFindings,
	commit
}: SiblingBatchBlacklistButtonGroupProps) {
	// Fetches the current blacklist status of only the displayed finding + of the finding and all its siblings
	const projectId = useProjectId();
	const findingAndSiblings = [finding as TrackedFinding, ...siblingFindings];
	const findingAndSiblingIDs = findingAndSiblings.map(finding => finding.id);
	const blacklistInfoFinding = useSuspenseQueryOperation(
		QUERY.getFlaggedFindingInfo(projectId, finding.id, { t: commit })
	);
	const blacklistInfoAll = useSuspenseQueryOperation(
		QUERY.getFlaggedFindingsInfos(projectId, { t: commit }, findingAndSiblingIDs)
	);
	const resolvedBlacklistInfo = findingAndSiblings.map(f => blacklistInfoAll.find(b => b.findingId === f.id) ?? null);

	// Fetches the permissions for toleration & declaring false-positives for the user
	const {
		detailedPermissions: { TOLERATION: tolerationPermissions, FALSE_POSITIVE: falsePositivePermissions }
	} = useBlacklistPermissions(findingAndSiblings, resolvedBlacklistInfo);
	const canEditTasks = useUserPermissionInfo().hasProjectPermission(projectId, EProjectPermission.EDIT_TASKS);
	return (
		<>
			{blacklistInfoAll.length > 0 &&
			(tolerationPermissions.includes('Unflag') || falsePositivePermissions.includes('Unflag')) ? (
				<UnblacklistButton
					title="Remove all siblings from False Positives/Tolerated"
					content="Remove all siblings from False Positives/Tolerated"
					data-testid="button-batch-unblacklist"
					commit={commit}
					findingsList={findingAndSiblings}
					blacklistInfo={blacklistInfoAll}
				/>
			) : null}
			{resolvedBlacklistInfo.some(f => f?.type !== EFindingBlacklistType.FALSE_POSITIVE.name) &&
			(falsePositivePermissions.includes('Mark') || falsePositivePermissions.includes('Suggest')) ? (
				<BlacklistButton
					title="Mark all as False Positives"
					content="Mark all as False Positives"
					data-testid="button-batch-false-positive"
					commit={commit}
					findingsList={findingAndSiblings}
					blackListType={EFindingBlacklistType.FALSE_POSITIVE}
				/>
			) : null}
			{(resolvedBlacklistInfo.some(f => f?.type !== EFindingBlacklistType.TOLERATION.name) &&
				tolerationPermissions.includes('Mark')) ||
			tolerationPermissions.includes('Suggest') ? (
				<BlacklistButton
					title="Tolerate all"
					content="Tolerate all"
					data-testid="button-batch-tolerated"
					commit={commit}
					findingsList={findingAndSiblings}
					blackListType={EFindingBlacklistType.TOLERATION}
				/>
			) : null}
			{blacklistInfoFinding == null && canEditTasks ? (
				<CreateTaskButton
					title="Add all sibling findings to a task"
					content="Add All to Task"
					data-testid="button-batch-create-task"
					commit={commit}
					blacklistInfo={blacklistInfoAll}
					findingsList={findingAndSiblings}
				/>
			) : null}
		</>
	);
}

/**
 * Props for SiblingSingleBlacklistActions consisting of the siblings, the commit, and the blacklist option for the
 * project
 */
type SiblingSingleBlacklistButtonGroupProps = {
	sibling: TrackedFinding;
	commit: UnresolvedCommitDescriptor;
};

/**
 * The single actions apply their effect (blacklist, unblacklist, add to task) to the currently selected siblings (of
 * the dropdown)
 */
export function SiblingSingleBlacklistButtonGroup({ sibling, commit }: SiblingSingleBlacklistButtonGroupProps) {
	// Fetches the current blacklist status of the current selected sibling
	const projectId = useProjectId();
	const queryBlacklistInfo = QUERY.getFlaggedFindingInfo(projectId, sibling.id, { t: commit });
	const blacklistInfo = useSuspenseQueryOperation(queryBlacklistInfo);

	// Fetches the permissions for toleration & declaring false-positives for the user
	const { canFlagAsToleration, canFlagAsFalsePositive } = useBlacklistPermissions([sibling], [blacklistInfo]);
	const canEditTasks = useUserPermissionInfo().hasProjectPermission(projectId, EProjectPermission.EDIT_TASKS);
	return (
		<ButtonGroup size="medium" basic>
			{(canFlagAsToleration || canFlagAsFalsePositive) && blacklistInfo ? (
				<UnblacklistButton
					title={`Remove this sibling from ${
						blacklistInfo.type === EFindingBlacklistType.FALSE_POSITIVE.name
							? 'False Positives'
							: 'Tolerated'
					}`}
					data-testid="button-single-unblacklist"
					commit={commit}
					findingsList={[sibling]}
					blacklistInfo={blacklistInfo}
				/>
			) : (
				<>
					{canFlagAsFalsePositive && blacklistInfo?.type !== EFindingBlacklistType.FALSE_POSITIVE.name ? (
						<BlacklistButton
							title="Flag this sibling as False Positive"
							data-testid="button-single-false-positive"
							commit={commit}
							findingsList={[sibling]}
							blackListType={EFindingBlacklistType.FALSE_POSITIVE}
						/>
					) : null}
					{canFlagAsToleration && blacklistInfo?.type !== EFindingBlacklistType.TOLERATION.name ? (
						<BlacklistButton
							title="Flag this sibling as Tolerated"
							data-testid="button-single-tolerated"
							commit={commit}
							findingsList={[sibling]}
							blackListType={EFindingBlacklistType.TOLERATION}
						/>
					) : null}
				</>
			)}
			{blacklistInfo == null && canEditTasks ? (
				<CreateTaskButton
					title="Add this sibling findings to a task"
					data-testid="button-single-create-task"
					commit={commit}
					findingsList={[sibling]}
				/>
			) : null}
		</ButtonGroup>
	);
}

/** The props for the single action button group consisting of commit, the finding, and its selected sibling */
type SiblingSingleActionButtonGroupProps = {
	commit: UnresolvedCommitDescriptor;
	finding: TrackedFindingWithDiffInfo;
	sibling: TrackedFinding;
};

/**
 * The single action button group consist of three buttons: compare full screen (compare view), view in file and go to
 * sibling's finding details
 */
export function SiblingSingleActionButtonGroup({ commit, finding, sibling }: SiblingSingleActionButtonGroupProps) {
	const project = useProjectId();
	const comparisonLink = getComparisonLink(
		finding.location,
		sibling.location,
		commit,
		project,
		finding.id,
		sibling.id
	);
	return (
		<ButtonGroup size="medium" basic>
			{comparisonLink != null ? (
				<Button
					icon="text width"
					title="Compare Full Screen"
					data-testid="button-compare-siblings"
					as={TeamscaleLink}
					to={comparisonLink}
				/>
			) : null}
			<Button
				icon="file alternate"
				title="View in File"
				data-testid="button-view-in-file"
				as={TeamscaleLink}
				to={
					FindingsUtils.isSpecItemFindingLocation(sibling.location)
						? Links.specItem(project, sibling.location.issueId, { commit })
						: PathUtils.getLinkToFileLocation(project, sibling.location, commit)
				}
			/>
			<Button
				icon="paper plane"
				title="To Finding"
				data-testid="button-to-finding"
				as={TeamscaleLink}
				to={Links.findingDetails(project, sibling.id, { commit })}
			/>
		</ButtonGroup>
	);
}
